import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import statisticsClient from '../../clients/statistics';
import { DISTRICTS } from '../Objects/components/Form/components/DistrictSelectField';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  card: {
    padding: theme.spacing(2),
    height: '100%',
  },
  cardValue: {
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  tableWrap: {
    maxHeight: 400,
  },
  note: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
}));

function StatCard({ title, value, subtitle }) {
  const classes = useStyles();
  return (
    <Paper className={classes.card} variant="outlined">
      <Typography color="textSecondary" gutterBottom variant="body2">
        {title}
      </Typography>
      <Typography className={classes.cardValue} component="span">
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

const Statistics = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await statisticsClient.getReport(selectedDistrict));
      } catch (err) {
        enqueueSnackbar('Nepavyko gauti statistikos. Bandykite vėliau.', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [enqueueSnackbar, selectedDistrict]);

  if (loading) {
    return (
      <div className={classes.root}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary || {};
  const activeDistrict = data.filter?.district || null;

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={2}
        alignItems="flex-end"
        justify="space-between"
        className={classes.section}
      >
        <Grid item xs={12} md="auto">
          <Typography variant="h5">Statistikos ataskaita</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="statistics-district-label">Savivaldybė</InputLabel>
            <Select
              labelId="statistics-district-label"
              label="Savivaldybė"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
            >
              <MenuItem value="">
                <em>Visos savivaldybės</em>
              </MenuItem>
              {DISTRICTS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {activeDistrict && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Rodomi duomenys tik pasirinktai savivaldybei: {activeDistrict}
        </Typography>
      )}

      <Grid container spacing={2} className={classes.section}>
        {/* First row: Total routes, Total objects, New objects added */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Viso maršrutų" value={s.totalRoutes ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Viso objektų" value={s.totalObjects ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pridėta naujų objektų"
            value={s.peopleAddedNewObjects ?? 0}
            subtitle="Siūlytų objektų skaičius"
          />
        </Grid>
        {/* Second row: Routes visited, Landmarks visited, Registered users */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Keliavo maršrutais"
            value={s.totalUniqueRouteTravels ?? 0}
            subtitle="Unikalių vizitų (iš programėlės)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Lankė objektus"
            value={s.totalUniqueObjectVisits ?? 0}
            subtitle="Unikalių vizitų (iš programėlės)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Užsiregistravę vartotojai"
            value={s.registeredUsers ?? 0}
            subtitle={
              activeDistrict
                ? 'Visi sistemos vartotojai (filtras netaikomas)'
                : undefined
            }
          />
        </Grid>
        {/* Third row: Routes reviewed, Objects reviewed, Good reviews for tourism services providers */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Maršrutų įvertinimai (iš viso)"
            value={s.totalRouteRatings ?? 0}
            subtitle="Įvertinimų skaičius"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Objektų įvertinimai (iš viso)"
            value={s.totalObjectRatings ?? 0}
            subtitle="Įvertinimų skaičius"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Gerų atsiliepimų (≥4 žvaigždutės)"
            value={s.totalGoodReviews ?? 0}
            subtitle="Objektams / paslaugų teikėjams"
          />
        </Grid>
        {/* Fourth row: Total gold coins collected, Total gold coins spent, Total gold coins collected by unique visitors */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Iš viso surinkta aukso monetų"
            value={s.totalCoinsReceived ?? 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Iš viso išleista aukso monetų"
            value={s.totalCoinsSpent ?? 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Unikalių lankytojų surinko aukso monetų"
            value={s.totalUniqueUsersCollectedCoins ?? 0}
            subtitle="Objektų lankymas (surinktos aukso monetos)"
          />
        </Grid>
        {/* Fifth row: Paid routes purchased */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Nupirkti mokami maršrutai"
            value={s.totalPaidRoutePurchases ?? 0}
            subtitle="Sėkmingai įsigijo mokamus maršrutus"
          />
        </Grid>
      </Grid>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Populiariausi maršrutai (pagal įvertinimus)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Įvertinimų sk.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.popularRoutesByRating || []).map((r) => (
                <TableRow key={r.routeId}>
                  <TableCell>{r.title || '—'}</TableCell>
                  <TableCell>{r.district || '—'}</TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                </TableRow>
              ))}
              {(!data.popularRoutesByRating ||
                data.popularRoutesByRating.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Populiariausi maršrutai (pagal pirkimus)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Pirkimų sk.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.popularRoutesByPurchase || []).map((r) => (
                <TableRow key={r.routeId}>
                  <TableCell>{r.title || '—'}</TableCell>
                  <TableCell>{r.district || '—'}</TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                </TableRow>
              ))}
              {(!data.popularRoutesByPurchase ||
                data.popularRoutesByPurchase.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Keliavo maršrutais (unikalių lankytojų)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Unikalių lankytojų</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.routeVisitsByRoute || []).map((r) => (
                <TableRow key={r.routeId}>
                  <TableCell>{r.title || '—'}</TableCell>
                  <TableCell>{r.district || '—'}</TableCell>
                  <TableCell align="right">{r.uniqueVisitors}</TableCell>
                </TableRow>
              ))}
              {(!data.routeVisitsByRoute ||
                data.routeVisitsByRoute.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Daugiausiai lankyti objektai (pagal surinktas monetas)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Lankytojų</TableCell>
                <TableCell align="right">Surinkta monetų</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.mostVisitedObjects || []).map((o) => (
                <TableRow key={o.objectId}>
                  <TableCell>{o.title || '—'}</TableCell>
                  <TableCell>{o.district || '—'}</TableCell>
                  <TableCell align="right">{o.uniqueVisitors}</TableCell>
                  <TableCell align="right">{o.totalCoinsReceived}</TableCell>
                </TableRow>
              ))}
              {(!data.mostVisitedObjects ||
                data.mostVisitedObjects.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Lankė objektus (unikalių lankytojų iš programėlės)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Unikalių lankytojų</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.objectVisitsByObject || []).map((o) => (
                <TableRow key={o.objectId}>
                  <TableCell>{o.title || '—'}</TableCell>
                  <TableCell>{o.district || '—'}</TableCell>
                  <TableCell align="right">{o.uniqueVisitors}</TableCell>
                </TableRow>
              ))}
              {(!data.objectVisitsByObject ||
                data.objectVisitsByObject.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Aukso monetos pagal savivaldybę (paskirstytos lankytojams)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Monetų iš viso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.goldCoinsAllocatedByDistrict || []).map((row) => (
                <TableRow key={row.district}>
                  <TableCell>{row.district}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                </TableRow>
              ))}
              {(!data.goldCoinsAllocatedByDistrict ||
                data.goldCoinsAllocatedByDistrict.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Išleistos monetos pagal savivaldybę (paslaugų teikėjams)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Išleista monetų</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.coinsSpentByDistrict || []).map((row) => (
                <TableRow key={row.district}>
                  <TableCell>{row.district}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                </TableRow>
              ))}
              {(!data.coinsSpentByDistrict ||
                data.coinsSpentByDistrict.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Daugiausiai įvertinti objektai (įvertinimų sk.)
        </Typography>
        <TableContainer
          className={classes.tableWrap}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pavadinimas</TableCell>
                <TableCell>Savivaldybė</TableCell>
                <TableCell align="right">Įvertinimų</TableCell>
                <TableCell align="right">Gerų (≥4)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.mostRatedObjects || []).map((o) => (
                <TableRow key={o.objectId}>
                  <TableCell>{o.title || '—'}</TableCell>
                  <TableCell>{o.district || '—'}</TableCell>
                  <TableCell align="right">{o.totalRatings}</TableCell>
                  <TableCell align="right">{o.goodReviews}</TableCell>
                </TableRow>
              ))}
              {(!data.mostRatedObjects ||
                data.mostRatedObjects.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4}>Nėra duomenų</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Paper className={classes.note} variant="outlined">
        <Typography variant="subtitle2" gutterBottom>
          Ką rodo ši ataskaita
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              Maršrutų statistika remiasi įvertinimais ir mokamų maršrutų
              pirkimais (nėra „nukeliavo maršrutą“ įvykio).
            </li>
            <li>
              Objektų / paslaugų teikėjų lankymas – tik tie, kurie surinko aukso
              monetų (vienas įrašas vienam lankytojui ir objektui).
            </li>
            <li>
              „Gerų atsiliepimų“ skaičius – įvertinimai ≥4 žvaigždutės. „Patiko“
              = įvertinimų skaičius.
            </li>
            <li>
              „Pridėta naujų objektų“ = siūlytų objektų (object-suggested)
              skaičius.
            </li>
          </ul>
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}
        >
          Statistikos, kurių negalima pateikti be papildomo sekimo programėlėje
          (pvz. kelionių maršrutais, lankymas informaciniuose languose,
          pasidalinimai, socialiniai tinklai, atsiliepimai apie programėlę),
          aprašytos failė STATISTICS_APPOINTMENT.md.
        </Typography>
      </Paper>
    </div>
  );
};

export default Statistics;
